import { useForm } from 'react-hook-form-mui';
import FormButtons from 'src/presentations/components/molecules/rhf-button/FormButtons';
import { DeviationQuestionDto, DeviationQuestionUserResponseDto } from 'src/generated/services/TFinancialApi';
import React, { ReactNode, useEffect, useMemo, useRef, useCallback } from 'react';
import { Box } from '@mui/material';
import IconEdit from 'src/assets/images/icons/edit-2.svg';
import IconButton from '@mui/material/IconButton';

import { ErrorBoundary } from 'react-error-boundary';
import clsx from 'clsx';
import { EActionDetailNotice } from 'src/presentations/components/organisms/actionDetails/Notice';
import { FormContainer } from 'src/presentations/components/organisms/forms/FormContainer';
import { FormFieldProvider } from 'src/presentations/components/organisms/forms/FormFieldContext';
import { QuestionnaireFormBuilder } from './QuestionnaireFormBuilder';
import { useQuestionnaireData } from './hooks/useQuestionnaireData';
import { useQuestionnaireValidation } from './hooks/useQuestionnaireValidation';
import { useQuestionnaireFields } from './hooks/useQuestionnaireFields';
import { processFormValuesToAnswers } from './utils/answerProcessor';
import { processQuestionsRecursively } from './utils/questionMapper';

export type QuestionnaireTheme = 'general' | 'dispute' | 'custom';

interface IQuestionnaireProps {
  isEditable?: boolean;
  isReadOnly?: boolean;
  isDisableSubmit?: boolean;
  title: string;
  questions: DeviationQuestionDto[];
  answers: DeviationQuestionUserResponseDto[];
  onSubmit: (data: DeviationQuestionUserResponseDto[], formData?: any) => Promise<void>;
  onCancel?: () => void;
  otherValidate?: () => boolean;
  onClickEdit: (event: React.MouseEvent) => void;
  moreActions?: ReactNode;
  theme?: QuestionnaireTheme;
  afterFields?: ReactNode;
  additionalDefaultValues?: Record<string, any>;
  additionalValidationSchema?: any; // Yup schema object to merge with questionnaire validation
  deviationId?: string; // Add deviationId prop
  setupCustomInputs?: (noticeEl: ReactNode, inputsEl: ReactNode, disabled?: boolean) => ReactNode;
  hideActionButtons?: boolean; // Hide edit and more actions buttons
  currentDeviationIndex?: number; // Current deviation position (0-indexed)
  totalDeviations?: number; // Total number of deviations
  onNavigateNext?: () => void; // Callback to navigate to next deviation
  onFormValuesChange?: (formValues: Record<string, any>) => void; // Callback when form values change
}

export const Questionnaire: React.FC<IQuestionnaireProps> = (props) => (
  <ErrorBoundary
    fallbackRender={({ error }) => (
      <div>
        <p>Something went wrong:</p>
        <pre style={{ color: 'red' }}>{error.message}</pre>
      </div>
    )}
  >
    <QuestionnaireInternal {...props} />
  </ErrorBoundary>
);

const QuestionnaireInternal: React.FC<IQuestionnaireProps> = ({
  title,
  questions,
  answers,
  onSubmit,
  isEditable = true,
  isReadOnly = false,
  isDisableSubmit,
  onCancel,
  otherValidate,
  moreActions,
  afterFields,
  theme = 'general',
  additionalDefaultValues = {},
  additionalValidationSchema,
  onClickEdit,
  deviationId, // Add deviationId parameter
  setupCustomInputs,
  hideActionButtons = false,
  currentDeviationIndex,
  totalDeviations,
  onNavigateNext,
  onFormValuesChange,
}) => {
  // Calculate if this is the last deviation
  const isLastDeviation = currentDeviationIndex !== undefined && totalDeviations !== undefined && currentDeviationIndex === totalDeviations - 1;

  // Dynamic button text based on deviation position
  const submitButtonText = isLastDeviation ? 'Save' : 'Save & Next';
  // Get initial default values from answers
  const { defaultValues } = useQuestionnaireData({
    questions,
    answers,
    additionalDefaultValues,
  });

  // Create a ref to store the latest validation schema
  const validationSchemaRef = useRef<any>(null);

  // Create dynamic resolver that always uses the latest schema
  const dynamicResolver = useCallback(async (data: any, context: any, options: any) => {
    if (!validationSchemaRef.current) {
      return { values: data, errors: {} };
    }

    try {
      const values = await validationSchemaRef.current.validate(data, { abortEarly: false });
      return { values, errors: {} };
    } catch (err: any) {
      return {
        values: {},
        errors: err.inner.reduce(
          (allErrors: any, currentError: any) => ({
            ...allErrors,
            [currentError.path]: {
              type: currentError.type ?? 'validation',
              message: currentError.message,
            },
          }),
          {}
        ),
      };
    }
  }, []);

  // Initialize form with dynamic resolver
  const formContext = useForm<Record<string, string>>({
    shouldUnregister: false,
    defaultValues,
    resolver: dynamicResolver,
  });

  // Watch form values for dynamic field visibility and validation
  const formValues = formContext.watch();

  // Notify parent when form values change
  useEffect(() => {
    if (onFormValuesChange) {
      onFormValuesChange(formValues);
    }
  }, [formValues, onFormValuesChange]);

  // Recalculate visible fields based on current form values
  const { visibleFieldConfigs } = useQuestionnaireData({
    questions,
    answers,
    formValues,
    additionalDefaultValues,
  });

  // Convert to field definitions
  const fieldDefinitions = useQuestionnaireFields({
    fieldConfigs: visibleFieldConfigs,
    isReadOnly,
  });

  // Generate validation schema for current visible fields
  const questionnaireValidationSchema = useQuestionnaireValidation(fieldDefinitions);

  // Merge questionnaire validation with additional validation schema if provided
  const validationSchema = useMemo(() => {
    if (additionalValidationSchema) {
      return questionnaireValidationSchema.shape(additionalValidationSchema.fields);
    }
    return questionnaireValidationSchema;
  }, [questionnaireValidationSchema, additionalValidationSchema]);

  // Update the ref whenever validation schema changes
  useEffect(() => {
    validationSchemaRef.current = validationSchema;
  }, [validationSchema]);

  return (
    <FormContainer
      isReadOnly={isReadOnly}
      formContext={formContext}
      onError={() => {
        // OnError
      }}
      onSuccess={async (formData) => {
        let isError = false;

        if (otherValidate) {
          isError = otherValidate();
        }

        if (!isError) {
          // Get all form values including watched values
          const allFormValues = formContext.getValues();
          const currentFormValues = { ...defaultValues, ...formData, ...allFormValues };

          const currentFieldConfigs = processQuestionsRecursively(questions, currentFormValues);

          // Convert form data to DeviationQuestionUserResponseDto format using all form values
          const userResponse = processFormValuesToAnswers(allFormValues, currentFieldConfigs);

          await onSubmit(userResponse, allFormValues);

          // Auto-navigate to next deviation if not last, otherwise close form

          if (!isLastDeviation && onNavigateNext) {
            onNavigateNext();
          } else if (onCancel) {
            onCancel();
          }
        }
      }}
    >
      <FormFieldProvider showUCTNotice={EActionDetailNotice.NONE}>
        <div className={clsx('flex flex-col gap-8', theme === 'general' && 'mb-20 xl:mb-24 p-14 xl:p-16')}>
          <Box className={clsx(theme === 'general' && 'container border-t border-neutral-20 pt-16 xl:pt-20 px-5 xl:px-7')}>
            <div className='flex items-center mb-20'>
              <h3 className='text-14 xl:text-16 leading-18 xl:leading-20 font-bold'>{title}</h3>
              {!hideActionButtons && (
                <div className='ml-auto pl-14 xl:pl-16 flex items-center gap-12'>
                  <>{moreActions}</>
                  {isReadOnly && isEditable && onClickEdit && (
                    <IconButton onClick={onClickEdit}>
                      <img src={IconEdit} alt='' className='w-20 xl:w-24' />
                    </IconButton>
                  )}
                </div>
              )}
            </div>

            <QuestionnaireFormBuilder
              questions={questions}
              answers={answers}
              additionalDefaultValues={additionalDefaultValues}
              isEditable={isEditable}
              deviationId={deviationId}
              setupCustomInputs={setupCustomInputs}
            />

            {afterFields}

            {!isReadOnly && isEditable && (
              <div className='mt-14 xl:mt-16 pt-14 xl:pt-16'>
                <FormButtons
                  onCancel={() => {
                    if (onCancel) {
                      onCancel();
                    }
                  }}
                  disabledCancel={!isReadOnly && isEditable && (!answers || answers?.length === 0)}
                  allowSubmitPristine
                  disabled={isDisableSubmit}
                  submitButtonText={submitButtonText}
                />
              </div>
            )}
          </Box>
        </div>
      </FormFieldProvider>
    </FormContainer>
  );
};
